import type { ChartPoint } from '../../utils/chartMath';

export type { ChartPoint };

export interface ChartSeries {
  id: string;
  label: string;
  /** Ascending by `t` (epoch ms). Null values render as gaps, never as zero. */
  points: ChartPoint[];
  /** A CSS colour — prefer a `var(--…)` token so it flips with the theme. */
  color: string;
  /** Area fill is honoured only for a single-series chart; overlays stay as lines. */
  kind?: 'line' | 'area';
  dashed?: boolean;
  /** Formats this series' values in the tooltip. Falls back to the chart-level formatter. */
  valueFormat?: (value: number) => string;
  hidden?: boolean;
}

/**
 * The module's series palette.
 *
 * Every entry is a theme token, so the same chart reads correctly on both grounds with no
 * JavaScript involved. The first colour is the accent, matching the rest of ARGUS's charts.
 */
export const SERIES_COLORS = [
  'var(--accent-300)',
  'var(--tone-success-text)',
  'var(--tone-amber-text)',
  'var(--tone-info-text)',
  'var(--tone-orange-text)',
  'var(--tone-danger-text)',
  // Purple sits last: the accent the primary series uses is itself purple, so bringing this
  // forward would put two near-identical lines beside each other in a small comparison.
  'var(--tone-purple-text)',
] as const;

/** Colour for a metric, kept fixed so a metric always reads the same across the module. */
export const METRIC_COLORS = {
  players: 'var(--accent-300)',
  tps: 'var(--tone-success-text)',
  mspt: 'var(--tone-purple-text)',
  popularity: 'var(--tone-amber-text)',
} as const;

/**
 * The network total is not one of the servers, so it gets a colour outside the series rotation
 * and is always drawn dashed. However many overlays are on, it stays visually distinct.
 */
export const NETWORK_SERIES_COLOR = 'var(--tone-neutral-text)';

export const CHART_GRID_COLOR = 'var(--border-subtle)';
export const CHART_LABEL_COLOR = 'var(--text-muted)';
export const CHART_POINT_STROKE = 'var(--bg-elevated)';

/**
 * A fixed viewBox width, matching `VoteChart` and `PackTimeseriesChart`.
 *
 * Rendering into a constant coordinate space avoids a `ResizeObserver` and a measure-then-render
 * pass. Strokes carry `vectorEffect="non-scaling-stroke"` so a 2px line stays 2px however wide
 * the container scales it.
 */
export const VIEWBOX_WIDTH = 900;

export const CHART_PADDING = { top: 16, right: 20, bottom: 30, left: 52 };

/** Enough samples for sub-pixel fidelity in a 900-unit viewBox, and cheap to path. */
export const DEFAULT_MAX_POINTS = 720;
